/**
 * Entity snapshots: what the server tells each client about the world every tick, inside the
 * client's interest radius (docs/ONLINE.md §3).
 *
 * Wire format (protocol v2; v3 adds the action cue in player flags; v4 the held weapon, the aim bit
 * and the weapon cues; v5 server-run police: crewed unit cars carry VehicleFlag.unit and foot officers
 * are entities of kind 2) — per client, relative to what
 * that client already has:
 *   e  full tuples for entities that ENTERED view (or everything on a keyframe)
 *   d  delta tuples `[id, mask, ...changed fields]` for known entities whose fields changed
 *   g  ids that LEFT view
 *   m  one-time metadata (name, spec, paint, owner; protocol v16: a car's spray-bay mods) for entities
 *      that entered view, or whose look changed (respray, spray bay: the server drops their baseline)
 * Entities in view but unchanged are not sent at all (a parked car costs 0 bytes per tick).
 * The transport (WebSocket) is reliable and ordered, so deltas always apply to the previous state;
 * periodic keyframes are a safety net only. Apply snapshots with `SnapshotState`.
 *
 * Tuples are quantised: positions in cm, heading in milliradians, velocity in cm/s.
 */
import type { WireMods } from './garage';

export const EntityKind = { player: 0, vehicle: 1, officer: 2 } as const;
export type EntityKindId = (typeof EntityKind)[keyof typeof EntityKind];

/** Player flag bits. */
export const PlayerFlag = {
  inVehicle: 1 << 0,
  /** Can't be harmed by players (spawn protection or safe zone). */
  spawnProtected: 1 << 1,
  /** Disconnected; avatar still in the world for the grace window. */
  lingering: 1 << 2,
  down: 1 << 3,
  /** Recently attacked someone: no safe-zone protection for a while. */
  aggressor: 1 << 4,
  /** Bits 8..15 carry the emote being played (0 = none); see EMOTES in shared/gangs.ts. */
  emoteShift: 8,
  /** Bits 16..19 carry the public action cue (PlayerAction, 0 = none). */
  actionShift: 16,
  /** Flips on every new cue, so the same action twice in a row (two punches) still reads as two. */
  actionToggle: 1 << 20,
  /** Bits 21..23 carry the weapon in hand (protocol v4): index into WEAPON_WIRE (shared/weapons.ts), 0 = fists. */
  weaponShift: 21,
  /** Weapon raised (aiming a gun / guard with a melee weapon). */
  aiming: 1 << 24,
  /** The bits a feature module may set through `Presence.pubFlags` (weapon + aim). */
  moduleMask: (0x7 << 21) | (1 << 24),
} as const;

/** Weapon in hand (index into WEAPON_WIRE, 0 = fists) from a player's flags. */
export const weaponOfFlags = (flags: number): number => (flags >> PlayerFlag.weaponShift) & 0x7;

/** Emote id (1-based index into EMOTES, 0 = none) from a player's flags. */
export const emoteOfFlags = (flags: number): number => (flags >> PlayerFlag.emoteShift) & 0xff;

/**
 * Public action cue (protocol v3): what other players should SEE this avatar doing, set by the server
 * from outcomes it already decided (seat granted, break-in pending, exit, PvP hit, arrest, death). It is
 * presentation only; nothing reads it back as state. Carried in the player's flags (bits 16..20), so it
 * rides the existing delta compression: a cue costs one flags delta when it starts and one when it ends.
 */
export const PlayerAction = {
  none: 0,
  /** Getting in (own/unlocked car, passenger seat, claimed ambient car). */
  enter: 1,
  /** Forcing a locked door; held until the break-in resolves or is cancelled. */
  breakin: 2,
  /** Hauling the door open and ducking in after a successful break-in. */
  steal: 3,
  exit: 4,
  /** Run down by another player's car. */
  knockdown: 5,
  busted: 6,
  /** Down until respawn. */
  wasted: 7,
  /** Melee swing at another player. */
  punch: 8,
  /** A gun went off (protocol v4; the shot itself travels as a `wfx` message for tracers). */
  fire: 9,
  /** Reloading the gun in hand; lasts the weapon's reload time. */
  reload: 10,
  /** A swing with a melee weapon (cosh, bat, crowbar, knife). */
  swing: 11,
} as const;
export type PlayerActionId = (typeof PlayerAction)[keyof typeof PlayerAction];
export const PLAYER_ACTION_MAX = 11;

/**
 * How long each cue lasts (ms) on the server, and therefore the length a client plays it over.
 * `breakin` and `wasted` last as long as their server state does (break-in timer, respawn delay);
 * the value here is only the client's progress scale.
 */
export const PLAYER_ACTION_MS: Readonly<Record<PlayerActionId, number>> = {
  0: 0,
  1: 650,
  2: 2500,
  3: 700,
  4: 450,
  5: 1100,
  6: 3000,
  7: 8000,
  8: 400,
  9: 280,
  10: 2400,
  11: 700,
};

/** Action cue (PlayerAction id) from a player's flags. Unknown ids (a newer server) read as none. */
export const actionOfFlags = (flags: number): PlayerActionId => {
  const a = (flags >> PlayerFlag.actionShift) & 0xf;
  return (a <= PLAYER_ACTION_MAX ? a : 0) as PlayerActionId;
};

/** Flags bits for an action cue (`seq` = cue counter; only its parity goes on the wire). */
export const actionFlags = (action: PlayerActionId, seq: number): number =>
  action === 0 ? 0 : ((action & 0xf) << PlayerFlag.actionShift) | (seq & 1 ? PlayerFlag.actionToggle : 0);

/** Identity of the cue in `flags` (action + toggle): a change means a new cue started. */
export const actionKeyOfFlags = (flags: number): number => (flags >> PlayerFlag.actionShift) & 0x1f;

/** Vehicle flag bits. */
export const VehicleFlag = {
  stolen: 1 << 0,
  wrecked: 1 << 1,
  siren: 1 << 2,
  /** Owned by a character or gang (persistent). */
  owned: 1 << 3,
  locked: 1 << 4,
  /** A police vehicle (stealing it is a serious crime). */
  police: 1 << 5,
  /**
   * A crewed police unit driven by the server (protocol v5, server/police/force.ts): nobody can get in,
   * `siren` = blue lamps, and its health is the unit's. Off-duty police cars (the station pool) don't
   * carry it.
   */
  unit: 1 << 6,
} as const;

/** Foot officer flag bits (entity kind 2, protocol v5). */
export const OfficerFlag = {
  /** Knocked down (by a car or a blow): lying flat. */
  down: 1 << 0,
  /** Armed response officer (revolver drawn). */
  armed: 1 << 1,
  /** Aiming at his suspect (the warning before a shot). */
  aiming: 1 << 2,
  /** Running (a chase) rather than walking. */
  running: 1 << 3,
  /** Toggles on every shot he fires (recoil pose; the round itself travels as a `pfx` message). */
  shot: 1 << 4,
} as const;

/** Server-owned vehicle category. */
export type VehicleCategory = 'civilian' | 'police' | 'owned' | 'gang';

/** Decoded player state. */
export interface PlayerState {
  kind: 0;
  id: number;
  x: number;
  z: number;
  h: number;
  vx: number;
  vz: number;
  flags: number;
  /** Vehicle entity id, 0 on foot. */
  vehicle: number;
  /** Public wanted level 0..4. */
  heat: number;
}

/** Decoded vehicle state. */
export interface VehicleState {
  kind: 1;
  id: number;
  x: number;
  z: number;
  h: number;
  vx: number;
  vz: number;
  flags: number;
  health: number;
  /** Driver's avatar entity id, 0 when nobody is driving. */
  driver: number;
}

/** Decoded foot officer state (server-run police, protocol v5). */
export interface OfficerState {
  kind: 2;
  id: number;
  x: number;
  z: number;
  h: number;
  vx: number;
  vz: number;
  /** OfficerFlag bits. */
  flags: number;
  /** Entity id of the unit car he belongs to (0 = none). */
  car: number;
}

export type EntityState = PlayerState | VehicleState | OfficerState;

/** One-time metadata for an entity entering view. */
export type EntityMeta =
  | { id: number; kind: 0; cid: number; name: string; gang?: string }
  | { id: number; kind: 1; spec: string; paint: number; cat: VehicleCategory; ownerCid?: number; ownerGang?: number; mod?: WireMods }
  | { id: number; kind: 2 };

/** Wire tuples: [kind, id, x, z, h, vx, vz, flags, a, b] (a/b = vehicle+heat, health+driver, or car+0 for an officer). */
export type PlayerTuple = [0, number, number, number, number, number, number, number, number, number];
export type VehicleTuple = [1, number, number, number, number, number, number, number, number, number];
export type OfficerTuple = [2, number, number, number, number, number, number, number, number, number];
export type EntityTuple = PlayerTuple | VehicleTuple | OfficerTuple;
/** `[id, mask, ...values]`: bit i of mask set ⇒ tuple field (i + 2) changed; values follow in field order. */
export type DeltaTuple = number[];

/** Number of delta-able fields (tuple indices 2..9). */
export const DELTA_FIELDS = 8;

export interface WireSnapshot {
  /** Server tick number. */
  k: number;
  /** Server time (ms) the snapshot was taken. */
  st: number;
  /** Last movement sequence the server processed for this client (for reconciliation). */
  ack: number;
  /** Full tuples (entered view / keyframe). */
  e?: EntityTuple[];
  /** Deltas for known entities. */
  d?: DeltaTuple[];
  /** Ids that left view. */
  g?: number[];
  /** Metadata for entities that entered view this snapshot. */
  m?: EntityMeta[];
  /** Set on keyframes: `e` lists EVERY entity in view; anything else known is stale. */
  kf?: 1;
}

const q100 = (n: number): number => Math.round(n * 100);
const q1000 = (n: number): number => Math.round(n * 1000);

/** Wrap an angle into (-π, π]. */
export function wrapAngle(a: number): number {
  const t = Math.PI * 2;
  let r = a % t;
  if (r <= -Math.PI) r += t;
  else if (r > Math.PI) r -= t;
  return r;
}

export function encodeEntity(e: EntityState): EntityTuple {
  if (e.kind === 0) {
    return [0, e.id, q100(e.x), q100(e.z), q1000(wrapAngle(e.h)), q100(e.vx), q100(e.vz), e.flags | 0, e.vehicle | 0, e.heat | 0];
  }
  if (e.kind === 2) return [2, e.id, q100(e.x), q100(e.z), q1000(wrapAngle(e.h)), q100(e.vx), q100(e.vz), e.flags | 0, e.car | 0, 0];
  return [1, e.id, q100(e.x), q100(e.z), q1000(wrapAngle(e.h)), q100(e.vx), q100(e.vz), e.flags | 0, Math.round(e.health), e.driver | 0];
}

export function decodeEntity(t: EntityTuple): EntityState {
  const base = { id: t[1], x: t[2] / 100, z: t[3] / 100, h: t[4] / 1000, vx: t[5] / 100, vz: t[6] / 100, flags: t[7] };
  if (t[0] === 0) return { kind: 0, ...base, vehicle: t[8], heat: t[9] };
  if (t[0] === 2) return { kind: 2, ...base, car: t[8] };
  return { kind: 1, ...base, health: t[8], driver: t[9] };
}

/** Delta from `prev` to `next` (same entity), or null when nothing changed. */
export function diffTuple(prev: EntityTuple, next: EntityTuple): DeltaTuple | null {
  let mask = 0;
  const values: number[] = [];
  for (let i = 0; i < DELTA_FIELDS; i++) {
    const v = next[i + 2]!;
    if (prev[i + 2] !== v) {
      mask |= 1 << i;
      values.push(v);
    }
  }
  return mask === 0 ? null : [next[1], mask, ...values];
}

/** Apply a delta to a tuple, returning a new tuple. */
export function applyDelta(prev: EntityTuple, d: DeltaTuple): EntityTuple {
  const out = [...prev] as EntityTuple;
  const mask = d[1]!;
  let j = 2;
  for (let i = 0; i < DELTA_FIELDS; i++) {
    if (mask & (1 << i)) out[i + 2] = d[j++]!;
  }
  return out;
}

export interface DecodedSnapshot {
  tick: number;
  serverTime: number;
  ack: number;
  /** Every entity currently in view (after applying this snapshot). */
  entities: EntityState[];
  /** Metadata received in this snapshot. */
  meta: EntityMeta[];
}

/**
 * Client-side snapshot accumulator: feed every `snap` in order; read the full view back.
 * Used by the game client (src/net), the test client and load-test bots.
 */
export class SnapshotState {
  readonly tuples = new Map<number, EntityTuple>();
  readonly meta = new Map<number, EntityMeta>();
  tick = 0;
  serverTime = 0;
  ack = 0;
  /** Deltas that referenced an unknown entity (should stay 0; non-zero means a protocol bug). */
  orphanDeltas = 0;

  apply(w: WireSnapshot): DecodedSnapshot {
    this.tick = w.k;
    this.serverTime = w.st;
    this.ack = w.ack;
    if (w.kf) this.tuples.clear();
    for (const id of w.g ?? []) {
      this.tuples.delete(id);
      this.meta.delete(id);
    }
    for (const m of w.m ?? []) this.meta.set(m.id, m);
    for (const t of w.e ?? []) this.tuples.set(t[1], t);
    for (const d of w.d ?? []) {
      const prev = this.tuples.get(d[0]!);
      if (!prev) {
        this.orphanDeltas++;
        continue;
      }
      this.tuples.set(d[0]!, applyDelta(prev, d));
    }
    return { tick: w.k, serverTime: w.st, ack: w.ack, entities: this.entities(), meta: w.m ?? [] };
  }

  entities(): EntityState[] {
    return [...this.tuples.values()].map(decodeEntity);
  }

  get(id: number): EntityState | undefined {
    const t = this.tuples.get(id);
    return t ? decodeEntity(t) : undefined;
  }
}
